import { Behavior, type Rect, Vector2 } from '@kite/engine';
import { randomPointIn } from '../placement';

/** How a {@link Wander} drifts. */
export interface WanderOptions {
  /** Where it is allowed to go. */
  readonly area: Rect;
  /** Travel speed in world units per second. */
  readonly speed: number;
  /** How long it keeps heading for one spot before choosing another. */
  readonly turnSeconds: number;
  /** Whether it rotates so its +X axis points the way it is traveling. */
  readonly faceDirection?: boolean;
}

const ARRIVAL_DISTANCE = 12;

/**
 * Drifts its entity from one random spot to another, forever.
 *
 * The scene's only source of motion that the child is not causing. A world where nothing moves unless
 * it is touched reads as broken; something wandering in the background reads as alive, and costs one
 * vector per frame.
 *
 * A timer as well as an arrival test, so a bee that is nudged past its target by anything still turns
 * instead of heading off the field.
 */
export class Wander extends Behavior {
  readonly #target = Vector2.zero();
  readonly #toTarget = Vector2.zero();
  readonly #options: WanderOptions;

  #secondsUntilTurn = 0;

  constructor(options: WanderOptions) {
    super();
    this.#options = options;
  }

  /** Picks the first destination, so the entity is already going somewhere on its first frame. */
  override onAttach(): void {
    this.#chooseTarget();
  }

  /** Steps one frame's worth of drift. */
  override onUpdate(deltaSeconds: number): void {
    const { speed, faceDirection } = this.#options;
    const position = this.entity.transform.position;

    this.#secondsUntilTurn -= deltaSeconds;

    // Retargeting aims the same frame rather than skipping it, or the bee stalls for a frame on
    // every turn and the drift reads as a stutter instead of a curve.
    if (this.#measureToTarget() <= ARRIVAL_DISTANCE || this.#secondsUntilTurn <= 0) {
      this.#chooseTarget();
    }

    const distance = this.#measureToTarget();
    if (faceDirection === true) {
      this.entity.transform.rotation = this.#toTarget.angle;
    }
    position.addScaled(this.#toTarget.normalize(), Math.min(speed * deltaSeconds, distance));
  }

  #chooseTarget(): void {
    randomPointIn(this.#options.area, this.#target);
    this.#secondsUntilTurn = this.#options.turnSeconds;
  }

  #measureToTarget(): number {
    this.#toTarget.copyFrom(this.#target).subtract(this.entity.transform.position);
    return this.#toTarget.length;
  }
}
